import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createDaemon,
  createDaemonShare,
  deleteDaemon,
  fetchDaemonShareCandidates,
  fetchDaemonShares,
  fetchDaemons,
  fetchSharedDaemons,
  requestDaemonRestart,
  revokeDaemonShare,
  updateDaemonShare,
  type CreateDaemonShareInput,
  type UpdateDaemonShareInput,
  type CreateDaemonInput,
  type CreatedDaemon,
  type UpdateDaemonInput,
  updateDaemon,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

/**
 * 공유 조건/노출 에이전트 변경이 반영되는 모든 표면의 캐시를 무효화한다.
 *
 * 공유는 소유자 목록(shares)뿐 아니라 수신 프로젝트의 공유 러너 목록(shared),
 * 노출이 바뀐 AgentConfig 목록, 러너 요청 부트스트랩, 홈 부트스트랩의 러너 가용성까지 바꾼다.
 * 한 곳이라도 빠지면 저장은 됐는데 화면은 이전 상태로 남는다.
 */
const invalidateDaemonShareSurfaces = (
  queryClient: ReturnType<typeof useQueryClient>,
  share: { daemonId: string; projectId: string },
) => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.daemons.shares() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.daemons.shares(share.daemonId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.daemons.shared(share.projectId) });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.daemons.shareCandidates(share.daemonId, share.projectId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.agentConfigs.all(share.projectId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.all(share.projectId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.homeBootstrapPrefix });
};

export const useDaemons = (
  token: string | null,
  options?: { enabled?: boolean; refetchOnWindowFocus?: boolean; refetchInterval?: number | false },
) => {
  return useQuery({
    queryKey: queryKeys.daemons.all,
    queryFn: () => fetchDaemons(token as string),
    enabled: Boolean(token) && (options?.enabled ?? true),
    // hover 프리워밍 직후 모달 오픈 시 캐시를 신선한 것으로 보고 재요청을 생략하되,
    // 러너 온/오프라인 상태가 과도하게 낡지 않도록 전역 기본(5분)보다 짧게 잡는다.
    staleTime: 60_000,
    // 모달을 닫아 쿼리가 비활성화돼도 캐시를 오래 유지해 재오픈 시 재요청을 피한다.
    gcTime: 1_800_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchInterval: options?.refetchInterval,
  });
};

export const useDaemonShares = (token: string | null, daemonId?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.daemons.shares(daemonId),
    queryFn: () => fetchDaemonShares(token as string, daemonId),
    enabled: Boolean(token) && (options?.enabled ?? true),
  });
};

export const useSharedDaemons = (
  token: string | null,
  projectId: string,
  options?: { enabled?: boolean; refetchOnWindowFocus?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.daemons.shared(projectId),
    queryFn: () => fetchSharedDaemons(token as string, projectId),
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    // useDaemons와 동일: 프리워밍 캐시를 신선하게 보되 공유 러너 상태가 과도하게 낡지 않게.
    staleTime: 60_000,
    gcTime: 1_800_000,
    // 프로젝트 전환 시 이전 목록을 즉시 보여주며 백그라운드로 갱신한다.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

export const useDaemonShareCandidates = (
  token: string | null,
  daemonId: string,
  projectId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.daemons.shareCandidates(daemonId, projectId),
    queryFn: () => fetchDaemonShareCandidates(token as string, daemonId, projectId),
    enabled: Boolean(token) && daemonId.length > 0 && projectId.length > 0 && (options?.enabled ?? true),
  });
};

export const useCreateDaemonMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<CreatedDaemon, Error, CreateDaemonInput | undefined>({
    mutationFn: (input) => createDaemon(token as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemons.all });
    },
  });
};

export const useDeleteDaemonMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDaemon(token as string, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemons.all });
    },
  });
};

export const useUpdateDaemonMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDaemonInput }) => updateDaemon(token as string, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemons.all });
    },
  });
};

export const useCreateDaemonShareMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ daemonId, input }: { daemonId: string; input: CreateDaemonShareInput }) =>
      createDaemonShare(token as string, daemonId, input),
    onSuccess: (share) => {
      invalidateDaemonShareSurfaces(queryClient, share);
    },
  });
};

export const useUpdateDaemonShareMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ daemonId, shareId, input }: { daemonId: string; shareId: string; input: UpdateDaemonShareInput }) =>
      updateDaemonShare(token as string, daemonId, shareId, input),
    onSuccess: (share) => {
      invalidateDaemonShareSurfaces(queryClient, share);
    },
  });
};

export const useRevokeDaemonShareMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ daemonId, shareId }: { daemonId: string; shareId: string }) =>
      revokeDaemonShare(token as string, daemonId, shareId),
    onSuccess: (share) => {
      invalidateDaemonShareSurfaces(queryClient, share);
    },
  });
};

export const useRequestDaemonRestartMutation = (token: string | null) => {
  return useMutation({
    mutationFn: (id: string) => requestDaemonRestart(token as string, id),
  });
};
